import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import useApi from "../../hooks/useApi";
import { useUser } from "../../hooks/useUser";

export default function DirectLinkResumen() {
  const query = new URLSearchParams(useLocation().search);
  const token = query.get("intent");
  const cancelled = query.get("cancelled") === "1";
  const navigate = useNavigate();
  const { loadApi, loadingApi, loggedApi } = useApi();
  const { updateUser } = useUser();
  const [intent, setIntent] = useState(null);
  const [rcOptions, setRcOptions] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Solicitud no encontrada.");
      return;
    }
    loadApi(`general/direct-links/intent/${encodeURIComponent(token)}`, false, "get")
      .then(({ data }) => setIntent(data))
      .catch((e) => setError(e.message));
  }, [token]);

  useEffect(() => {
    if (!token || !loggedApi) {
      setRcOptions(null);
      return;
    }

    loadApi(
      `artista/direct-links/payment-options/${encodeURIComponent(token)}`,
      true,
      "get"
    )
      .then(({ data }) => setRcOptions(data))
      .catch(() => setRcOptions(null));
  }, [token, loggedApi]);

  const pagarConRadarCoins = async () => {
    setError("");
    try {
      const { data } = await loadApi(
        `artista/direct-links/pay-with-radarcoins/${encodeURIComponent(token)}`,
        true,
        "post",
        {}
      );
      await updateUser();
      navigate(
        `/artista/envios?pedido=${encodeURIComponent(data.envioId)}&direct=1`
      );
    } catch (e) {
      setError(e.message);
    }
  };

  const pagar = async () => {
    setError("");
    try {
      const { data } = await loadApi(
        `general/direct-links/intent/${encodeURIComponent(token)}/paypal`,
        false,
        "post",
        {}
      );
      window.location.href = data.url;
    } catch (e) {
      setError(e.message);
    }
  };

  if (!intent && !error) {
    return (
      <Stack minHeight="100vh" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (error && !intent) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="warning">{error}</Alert>
      </Container>
    );
  }

  const titulo =
    intent.contentType === "evento"
      ? intent.payload?.eventName
      : intent.payload?.releaseName;

  return (
    <Box minHeight="100vh" bgcolor="#f4f4f6" py={{ xs: 3, md: 6 }}>
      <Container maxWidth="sm">
        <Card sx={{ borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack alignItems="center" textAlign="center" spacing={1}>
              <Avatar src={intent.difusor?.profileimg || undefined} sx={{ width: 72, height: 72 }} />
              <Typography variant="h5" fontWeight={900}>
                Revisa tu solicitud
              </Typography>
              <Typography color="text.secondary">
                Antes de pagar, confirma que todo corresponde a lo que quieres solicitar.
              </Typography>
            </Stack>

            {cancelled && (
              <Alert severity="info" sx={{ mt: 3 }}>
                El pago fue cancelado. Tu solicitud sigue guardada y puedes intentarlo de nuevo.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2} mt={4}>
              <Box>
                <Typography variant="caption" color="text.secondary">Difusor</Typography>
                <Typography fontWeight={800}>{intent.difusor?.nombre_media}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Servicio</Typography>
                <Typography fontWeight={800}>{intent.service?.nombre}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {intent.contentType === "evento" ? "Evento" : "Lanzamiento"}
                </Typography>
                <Typography fontWeight={800}>{titulo}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Proyecto</Typography>
                <Typography fontWeight={800}>{intent.projectName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Responsable</Typography>
                <Typography>{intent.responsibleName}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Correo</Typography>
                <Typography>{intent.email}</Typography>
              </Box>
              <Alert severity="info">
                {intent.contentType === "evento"
                  ? "El difusor tendrá hasta 5 días para atender la solicitud, pero el plazo nunca se extiende más allá de la fecha del evento. Si vence sin respuesta, los 5 RadarCoins regresan a tu cuenta."
                  : "El difusor tendrá hasta 10 días para atender la solicitud. Si vence el plazo sin respuesta, los 5 RadarCoins regresan a tu cuenta de RadarFreim."}
              </Alert>

              <Divider />
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography fontWeight={900}>Total</Typography>
                <Box textAlign="right">
                  <Typography variant="h5" color="primary.main" fontWeight={900}>
                    USD $12.50
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    5 RadarCoins
                  </Typography>
                </Box>
              </Stack>

              {intent.accountExists && intent.accountReady === false ? (
                <Alert severity="warning">
                  {intent.accountIssue ||
                    "Esta cuenta necesita atención antes de continuar con el pago."}
                </Alert>
              ) : intent.status === "PAID" ||
                intent.status === "ORDER_CREATED" ||
                intent.status === "COMPLETED" ? (
                <Alert severity="success">
                  Este pedido ya fue pagado. Continúa con la activación de tu cuenta.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {rcOptions?.canUseRadarCoins && (
                    <>
                      <Alert severity="success">
                        Tienes {rcOptions.credits} RadarCoins disponibles. Puedes
                        usar 5 RC para enviar este pedido sin hacer otro pago.
                      </Alert>
                      <LoadingButton
                        size="large"
                        loading={loadingApi.includes(
                          `post__artista/direct-links/pay-with-radarcoins/${token}`
                        )}
                        onClick={pagarConRadarCoins}
                      >
                        Usar 5 RadarCoins
                      </LoadingButton>
                      <Divider>o</Divider>
                    </>
                  )}

                  {loggedApi &&
                    rcOptions &&
                    !rcOptions.canUseRadarCoins &&
                    rcOptions.status === "DRAFT" && (
                      <Alert severity="info">
                        Tu saldo actual es de {rcOptions.credits} RC. Puedes
                        completar este pedido con PayPal.
                      </Alert>
                    )}

                  <LoadingButton
                    disabled={
                      intent.accountExists && intent.accountReady === false
                    }
                    size="large"
                    variant={rcOptions?.canUseRadarCoins ? "outlined" : "contained"}
                    loading={loadingApi.includes(
                      `post__general/direct-links/intent/${token}/paypal`
                    )}
                    onClick={pagar}
                    startIcon={<CreditCardOutlinedIcon />}
                  >
                    Pagar con PayPal o tarjeta · USD $12.50
                  </LoadingButton>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    textAlign="center"
                  >
                    <strong>¿Quieres pagar con tarjeta?</strong> Si PayPal no te
                    muestra esa opción, te recomendamos abrir este enlace en una
                    ventana de navegación privada.
                  </Typography>
                </Stack>
              )}

              {intent.accountExists &&
                intent.accountReady !== false &&
                intent.status !== "PAID" &&
                intent.status !== "ORDER_CREATED" &&
                intent.status !== "COMPLETED" &&
                !loggedApi && (
                  <Alert severity="info">
                    Reconocimos tu cuenta de RadarFreim. Si inicias sesión y
                    tienes al menos 5 RadarCoins, podrás usarlos para este pedido.
                    Si prefieres pagar con PayPal, después solo tendrás que
                    iniciar sesión para vincularlo.
                  </Alert>
                )}

              <Typography variant="caption" color="text.secondary" textAlign="center">
                Después del pago podrás seguir el estado de este pedido y futuros
                envíos desde RadarFreim.
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
